import React from 'react';

import { Entry } from 'contentful';
import { NextPageWithLayout } from 'next';
import NextImage from 'next/image';
import ContentLoader from 'react-content-loader';

import { useRutilusQuery } from '../../api/graphql/useRutilusQuery';
import { AppDownloadSection } from '../../components/AppDownloadSection/AppDownloadSection';
import { BlogPostSection } from '../../components/Blog/BlogPostSection/BlogPostSection';
import { AppStoreButtons } from '../../components/Clickables/Buttons/AppStoreButtons/AppStoreButtons';
import { Container } from '../../components/Container/Container';
import { FindUSSpecies } from '../../components/FindUSSpecies/FindUSSpecies';
import { IAllStateSpeciesData } from '../../components/FindUSSpecies/helpers/buildStateDataSpecies';
import { Heading } from '../../components/Heading/Heading';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { PopularSpeciesNearYou } from '../../components/PopularSpeciesNearYou/PopularSpeciesNearYou';
import { QRCode } from '../../components/QRCode/QRCode';
import { Rating, RatingVariant } from '../../components/Rating/Rating';
import { SearchSpecies } from '../../components/Search/SearchSpecies/SearchSpecies';
import { SEOFishFamilies } from '../../components/SEOFishFamilies/SEOFishFamilies';
import { SEOLinksFooter } from '../../components/SEOFooter/SEOLinksFooter';
import { Text } from '../../components/Text/Text';
import { IOS_APP_RATING } from '../../constants/constants';
import { ISeoSpeciesFamilyItem } from '../../constants/species/types';
import { useGeolocation } from '../../contexts/GeolocationContext/GeolocationContext';
import { getEdgesFromConnection } from '../../helpers/graphql';
import { useClientHasRendered } from '../../helpers/hooks';
import { TypeBlogPostSkeleton } from '../../interfaces/contentful';
import {
  GetSpeciesFromList_species_edges,
  GetTopSpeciesNearLocation,
  GetTopSpeciesNearLocationVariables,
} from '../../interfaces/graphql';
import { getTopSpeciesNearLocation } from '../../queries/rutilus/Species';

import speciesBGDesktop from './assets/speciesBGDesktop.png';
import speciesBGMobile from './assets/speciesBGMobile.png';

const POPULAR_SPECIES_SECTION_TITLE = 'Most popular fish species near you';

export interface IDiscoverProps {
  defaultTopSpeciesEdgeList: GetSpeciesFromList_species_edges[];
  seoSpeciesFamilies: ISeoSpeciesFamilyItem[];
  seoSpeciesDefultFamily: ISeoSpeciesFamilyItem;
  stateSpeciesData: IAllStateSpeciesData;
  blogPostItems: Entry<TypeBlogPostSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>[] | undefined;
}

export const DiscoverSpecies: NextPageWithLayout<IDiscoverProps> = ({
  defaultTopSpeciesEdgeList,
  seoSpeciesFamilies,
  seoSpeciesDefultFamily,
  stateSpeciesData,
  blogPostItems,
}) => {
  const { isLoading: currentLocationLoading, location } = useGeolocation();
  const clientHasRendered = useClientHasRendered();

  const { data, loading } = useRutilusQuery<
    GetTopSpeciesNearLocation,
    GetTopSpeciesNearLocationVariables
  >(getTopSpeciesNearLocation, {
    variables: {
      first: 8,
      boundingCircle: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        center: location?.coordinates!,
        delta: 50,
      },
    },
    skip: currentLocationLoading,
  });
  const topSpeciesEdgeList = data ? getEdgesFromConnection(data.topSpeciesNearLocation) : [];

  return (
    <div>
      <MetaTags
        link="/discover/species"
        title="Fish species directory ➡️ Discover and locate your fish"
        description="Navigate our fish directory and explore the spots for your next big catch ✅ Fishbrain Discover ➡ Your ultimate fishing directory • Register for Free!"
        image={speciesBGMobile.src}
      />
      <Container className="-px-6 sm:-px-11 md:-px-14 grid w-full grid-cols-1 grid-rows-1">
        <div className="col-start-1 row-start-1">
          <NextImage
            alt="most liked catch image"
            className="size-full object-cover sm:hidden"
            src={speciesBGMobile}
            priority
            placeholder="blur"
            sizes="100vw"
          />
          <NextImage
            alt="most liked catch image"
            className="hidden size-full object-cover sm:block"
            src={speciesBGDesktop}
            placeholder="blur"
            sizes="(min-width: 1680px) 1552px, 100vw"
          />
        </div>
        <div className="col-start-1 row-start-1 flex flex-col items-center justify-center px-2 py-8 text-center sm:pb-2 sm:pt-4 md:pt-8">
          <Heading as="h1" variant="lg" className="relative mb-4 mt-0">
            Explore fish species
          </Heading>
          <SearchSpecies className="relative z-mid mx-auto w-11/12 max-w-[800px] sm:w-3/4" />
          <Text className="mt-3 px-12">Get the full experience by downloading the app</Text>
          {clientHasRendered ? (
            <div className="flex flex-col md:flex-row md:gap-x-6">
              <QRCode source="DiscoverSpecies" />
              <div className="flex flex-col items-center">
                <div className="flex justify-start">
                  <AppStoreButtons source="DiscoverSpecies" orientation="vertical" />
                </div>
                <Rating rating={IOS_APP_RATING} variant={RatingVariant.allStars} textSize="sm" />
                <Text variant="sm" className="mt-2 font-semibold">
                  37.6K ratings
                </Text>
              </div>
            </div>
          ) : (
            <ContentLoader
              uniqueKey="discover-species-rating-loader-key"
              speed={2}
              backgroundColor="#f3f3f3"
              foregroundColor="#ecebeb"
              height={113}
              width={160}
            >
              <rect x="0" y="0" width="400" height="250" />
            </ContentLoader>
          )}
        </div>
      </Container>

      <SEOFishFamilies
        seoSpeciesFamilies={seoSpeciesFamilies}
        seoSpeciesDefultFamily={seoSpeciesDefultFamily}
      />

      <FindUSSpecies stateSpeciesData={stateSpeciesData} />

      <PopularSpeciesNearYou
        sectionTitle={POPULAR_SPECIES_SECTION_TITLE}
        clickEventSource="" // TODO: https://fishbrain.atlassian.net/browse/FIB-41168
        clickEventSourceId="" // TODO: https://fishbrain.atlassian.net/browse/FIB-41168
        topSpeciesEdgeList={topSpeciesEdgeList}
        defaultSpeciesEdgeList={defaultTopSpeciesEdgeList}
        isLoading={loading || currentLocationLoading}
      />

      {blogPostItems && blogPostItems.length > 0 && (
        <BlogPostSection blogPostItems={blogPostItems} linkTo="/blog/species" />
      )}

      <div>
        {/* TODO: https://fishbrain.atlassian.net/browse/FIB-41168 */}
        <AppDownloadSection />
      </div>
      <SEOLinksFooter />
    </div>
  );
};
